import {
	VelocityClient,
	OracleSource,
	SlotDurationMs,
	msToSlotsNum,
} from '@velocity-exchange/sdk';
import {
	MarketId,
	UIMarket,
	getOracleCrankIxs as getOracleCrankIxsShared,
	OracleCrankDataFetcher,
	isPythOracle,
} from '@velocity-exchange/common';
import { TransactionInstruction } from '@solana/web3.js';
import { WalletName } from '@solana/wallet-adapter-base';
import Env from 'src/environmentVariables/EnvironmentVariables';
import { WALLET_NAME_SQUADS_X } from 'src/constants/wallets';
import { formatText, PRESETS } from '@velocity-exchange/common/format';
import { trimDisplayZeros } from 'src/utils/formatNumber/trimDisplayZeros';
import { fetchPythCranks } from './fetchPythCranks';

export const getOracleUrl = (
	oracleSource: OracleSource,
	symbol: string,
	_oraclePubKey: string,
	_isDevnet = false
): string => {
	if (isPythOracle(oracleSource)) {
		return `https://insights.pyth.network/price-feeds/Crypto.${symbol.toUpperCase()}%2FUSD`;
	} else {
		return '';
	}
};

export const getOracleOffsetLabel = (
	offset: number,
	excludePrefix?: boolean
) => {
	return `${!excludePrefix ? 'Oracle Price ' : ''}${
		offset < 0 ? '-' : '+'
	}$${trimDisplayZeros(formatText(Math.abs(offset), PRESETS.displayPrice), 2)}`;
};

const webCrankDataFetcher: OracleCrankDataFetcher = async (source, feedIds) => {
	const result = await fetchPythCranks(source, feedIds as string[] | number[]);
	return {
		success: result.success,
		data: result.body?.data,
	};
};

export const getOracleCrankIxs = async (
	marketIds: MarketId[],
	velocityClient: VelocityClient,
	currentWalletName: WalletName<string>,
	slot: {
		current: number;
		durationMs: SlotDurationMs;
		customStalenessThresholdMs?: number;
	},
	maxPythLazerCranks = 1,
	preceedingIxsCount = 2
): Promise<TransactionInstruction[]> => {
	if (Env.disablePervasiveOracleCrank) return [];

	if (currentWalletName === WALLET_NAME_SQUADS_X) return [];

	// The budget is wall-clock, so it converts at the live slot length. It is a
	// staleness ceiling and floors: cranking early costs an instruction, while
	// cranking late lets the program reject the fill.
	const stalenessThresholdSlots = msToSlotsNum(
		slot.customStalenessThresholdMs ?? Env.oracleCrankStalenessThresholdMs,
		slot.durationMs
	);

	const filteredConfigs = marketIds
		.map((id) => {
			const oracleData = id.isPerp
				? velocityClient.getOracleDataForPerpMarket(id.marketIndex)
				: velocityClient.getOracleDataForSpotMarket(id.marketIndex);
			return {
				marketId: id,
				slotLastUpdated: oracleData?.slot?.toNumber() ?? 0,
			};
		})
		.filter((idAndSlot) => {
			return idAndSlot.slotLastUpdated < slot.current - stalenessThresholdSlots;
		})
		.sort((a, b) => a.slotLastUpdated - b.slotLastUpdated)
		.map((idAndSlot) => {
			return idAndSlot.marketId.isPerp
				? UIMarket.perpMarkets[idAndSlot.marketId.marketIndex]
				: UIMarket.spotMarkets[idAndSlot.marketId.marketIndex];
		});

	return getOracleCrankIxsShared({
		marketConfigs: filteredConfigs,
		velocityClient: velocityClient,
		fetchCrankData: webCrankDataFetcher,
		maxPythLazerCranks,
		precedingIxsCount: preceedingIxsCount,
	});
};
